import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { useSession } from '../../contexts/SessionContext';

interface Project {
  projektnummer: string;
  name: string;
  billableHours: number;
  nonBillableHours: number;
  totalHours: number;
  billableRate: number;
}

interface YTDProjectsTableProps {
  projects: Project[];
  year: number;
  monthName: string;
  title?: string;
  isMonthFiltered?: boolean;
  onProjectSelect?: (projectNumber: string) => void;
  selectedProjectNumber?: string | null;
  selectedMonthForFilter?: number | null;
}

export function YTDProjectsTable({
  projects,
  year,
  monthName,
  title = "Alle Projekte",
  isMonthFiltered = false,
  onProjectSelect,
  selectedProjectNumber = null,
  selectedMonthForFilter = null
}: YTDProjectsTableProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const navigate = useNavigate();
  const { employeeId } = useSession();

  const handleProjectClick = (projectName: string, projectNumber: string) => {
    if (!employeeId || selectedMonthForFilter === null) {
      return;
    }

    const monthNumber = selectedMonthForFilter + 1;

    navigate({
      to: '/overview/leistungsnachweis',
      search: {
        project_number: projectNumber,
        month: monthNumber.toString(),
        year: year.toString(),
      },
    });
  };

  const handleProjectNameClick = (projectNumber: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onProjectSelect) {
      if (selectedProjectNumber === projectNumber) {
        onProjectSelect(null as any);
      } else {
        onProjectSelect(projectNumber);
      }
    }
  };

  const totalBillable = projects.reduce((sum, p) => sum + p.billableHours, 0);
  const totalNonBillable = projects.reduce((sum, p) => sum + p.nonBillableHours, 0);
  const totalHours = totalBillable + totalNonBillable;
  const overallBillableRate = totalHours > 0 ? (totalBillable / totalHours) * 100 : 0;

  const filteredProject = projects.find(p => p.projektnummer === selectedProjectNumber);

  return (
    <div className="ov-p-6">
      <div
        className="ov-flex ov-justify-between ov-items-center ov-mb-4 ov-cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="ov-flex ov-items-center ov-gap-3">
          <h3 className="ov-text-gray-900 ov-font-bold">
            {title} ({isMonthFiltered ? monthName : 'Year to Date'} {year})
            {filteredProject && (
              <span className="ov-text-green-600 ov-ml-2">(gefiltert: {filteredProject.projektnummer} - {filteredProject.name})</span>
            )}
          </h3>
          <div className="ov-text-sm ov-text-gray-600">
            {projects.length} {projects.length === 1 ? 'Projekt' : 'Projekte'}
          </div>
        </div>
        <button className="ov-text-gray-500 ov-hover:text-gray-700 ov-bg-transparent ov-border-none ov-outline-none">
          {isExpanded ? <ChevronUp className="ov-w-5 ov-h-5" /> : <ChevronDown className="ov-w-5 ov-h-5" />}
        </button>
      </div>

      {isExpanded && (
        <div
          className="ov-overflow-x-auto ov-max-h-500px ov-overflow-y-auto ov-pr-1"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#cbd5e1 #f1f5f9'
          }}
        >
          <table className="ov-w-full ov-text-xs">
            <thead className="ov-sticky ov-top-0 ov-bg-white ov-z-10">
              <tr className="ov-border-b ov-border-gray-200">
                <th className="ov-text-left ov-py-1-5 ov-px-2 ov-text-gray-600">Projekt-ID</th>
                <th className="ov-text-left ov-py-1-5 ov-px-2 ov-text-gray-600">Projektname</th>
                <th className="ov-text-left ov-py-1-5 ov-px-2 ov-text-gray-600"></th>
                <th className="ov-text-right ov-py-1-5 ov-px-2 ov-text-gray-600">Billable Stunden</th>
                <th className="ov-text-right ov-py-1-5 ov-px-2 ov-text-gray-600">Non-Billable Stunden</th>
                <th className="ov-text-right ov-py-1-5 ov-px-2 ov-text-gray-600">Gesamt</th>
                <th className="ov-text-right ov-py-1-5 ov-px-2 ov-text-gray-600">Billable Rate</th>
              </tr>
            </thead>
            <tbody>
              {projects.length === 0 ? (
                <tr>
                  <td colSpan={7} className="ov-py-8 ov-px-2 ov-text-center ov-text-gray-500">
                    Keine Projekte gefunden
                  </td>
                </tr>
              ) : (
                projects.map((project, index) => (
                  <tr
                    key={index}
                    className={`ov-border-b ov-border-gray-100 ov-hover:bg-gray-50 ${selectedProjectNumber === project.projektnummer ? 'ov-bg-green-100' : ''
                      }`}
                  >
                    <td className="ov-py-1-5 ov-px-2 ov-text-gray-900 ov-font-mono">{project.projektnummer}</td>
                    <td
                      className="ov-py-1-5 ov-px-2 ov-text-gray-700 ov-cursor-pointer ov-hover:text-blue-600 ov-font-medium"
                      onClick={(e) => handleProjectNameClick(project.projektnummer, e)}
                    >
                      {project.name}
                    </td>
                    <td className="ov-py-1-5 ov-px-2">
                      {isMonthFiltered && project.billableHours > 0 &&
                        <button
                          onClick={() => handleProjectClick(project.name, project.projektnummer)}
                          className="ov-px-2 ov-py-0-5 ov-bg-blue-600 ov-rounded ov-text-10px ov-hover:bg-blue-700 ov-transition-colors ov-border-none"
                          style={{ color: 'var(--accent)' }}
                          title="view Leistungsnachweis for selected project"
                        >
                          Rapport
                        </button>
                      }
                    </td>
                    <td className="ov-py-1-5 ov-px-2 ov-text-right ov-text-green-600">{project.billableHours}h</td>
                    <td className="ov-py-1-5 ov-px-2 ov-text-right ov-text-red-600">{project.nonBillableHours}h</td>
                    <td className="ov-py-1-5 ov-px-2 ov-text-right ov-text-gray-900">{project.totalHours}h</td>
                    <td className="ov-py-1-5 ov-px-2 ov-text-right">
                      <span className={`ov-inline-flex ov-items-center ov-gap-1 ov-px-1-5 ov-py-0-5 ov-rounded ov-text-10px ${project.billableRate >= 85
                          ? 'ov-bg-green-100 ov-text-green-700'
                          : project.billableRate >= 50
                            ? 'ov-bg-yellow-100 ov-text-yellow-700'
                            : 'ov-bg-red-100 ov-text-red-700'
                        }`}>
                        {project.billableRate.toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {projects.length > 0 && (
              <tfoot className="ov-sticky ov-bottom-0 ov-bg-white ov-z-10">
                <tr className="ov-border-t-2 ov-border-gray-300 ov-bg-gray-50">
                  <td className="ov-py-1-5 ov-px-2 ov-text-gray-900">Gesamt</td>
                  <td className="ov-py-1-5 ov-px-2 ov-text-gray-600">{projects.length} Projekte</td>
                  <td className="ov-py-1-5 ov-px-2"></td>
                  <td className="ov-py-1-5 ov-px-2 ov-text-right ov-text-green-600">{totalBillable}h</td>
                  <td className="ov-py-1-5 ov-px-2 ov-text-right ov-text-red-600">{totalNonBillable}h</td>
                  <td className="ov-py-1-5 ov-px-2 ov-text-right ov-text-gray-900">{totalHours}h</td>
                  <td className="ov-py-1-5 ov-px-2 ov-text-right">
                    <span className="ov-inline-flex ov-items-center ov-gap-1 ov-px-1-5 ov-py-0-5 ov-bg-blue-100 ov-text-blue-700 ov-rounded ov-text-10px">
                      {overallBillableRate.toFixed(1)}%
                    </span>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

    </div>
  );
}

